import React, { FC } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { Button, Spinner } from '@ems/client-design-system';
import { ILoadMoreBar } from './interfaces';

/**
 * A component that displays a load more button with loading spinner.
 *
 * @deprecated Use `componentsModern/LoadMoreBar` instead.
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} [props.isVisible=false] - Whether the load more bar should be visible.
 * @param {boolean} props.isLoadingMore - Whether more data is currently being loaded.
 * @param {Object} props.dispatcher - The dispatcher object for state updates.
 * @param {Function} props.loadMore - Function to load more data.
 * @param {number} props.resultSize - The size of results to fetch.
 * @param {string} props.endCursor - The cursor for pagination.
 * @param {string} props.sortString - The sort configuration string.
 * @param {string} props.filterString - The filter configuration string.
 * @param {boolean} props.showCandidates - Whether to show candidates.
 * @param {Object} [props.pcaData={}] - PCA data object.
 * @param {Object} props.permissions - User permissions object.
 * @param {boolean} [props.atcView=false] - Whether to use ATC view.
 * @param {string} props.loadMoreText - The text to display on the load more button.
 * @param {Object} props.featureFlags - Feature flags configuration.
 * @param {Object} props.availableFilters - Available filters configuration.
 * @returns {JSX.Element|null} The LoadMoreBar component or null if not visible or no endCursor.
 */
export const LoadMoreBar: FC<ILoadMoreBar> = ({
  isVisible = false,
  isLoadingMore,
  dispatcher,
  loadMore,
  resultSize,
  endCursor,
  sortString,
  filterString,
  showCandidates,
  pcaData = {},
  permissions,
  atcView = false,
  loadMoreText,
  featureFlags,
  availableFilters,
}) => {
  const client = useApolloClient();
  if (!isVisible || !endCursor) {
    return null;
  }

  return (
    <div className="load-more-bar">
      <Spinner loading={isLoadingMore}>
        <Button
          style="primary"
          onClick={() => {
            loadMore(client, dispatcher, {
              resultSize,
              endCursor,
              sortString,
              filterString,
              showCandidates,
              pcaData,
              permissions,
              atcView,
              featureFlags,
              availableFilters,
            });
          }}>
          {loadMoreText}
        </Button>
      </Spinner>
    </div>
  );
};
